import { useEffect, useRef, type RefObject } from "react";
import { useReactFlow, type Viewport } from "@xyflow/react";

// The board's hand: a press anywhere on the board moves the whole board.
//
// Owner, 2026-10-08: "the storyboard not being able to be grabbed by the hand
// or repositioned anywhere" (a PC with a mouse, sometimes a phone). React Flow
// pans only from the bare background: every card it can drag carries its
// `nopan` class on the card itself, so a press anywhere on a card, which on a
// 36-scene board is most of the board, could never move it. The earlier fixes
// (a hand tool, a bigger grip bar) all left that in place.
//
// So the board's drag is driven here, from pointer events, for the background
// and the cards alike (React Flow's own drag-to-pan is off in hand mode):
//   mouse   left-drag anywhere that is not a control; middle-drag anywhere;
//           Space held + drag anywhere (a click under it never fires)
//   touch   one finger drags (from a button too: the tap is then dropped),
//           two fingers pinch-zoom and pan together, wherever they land
//   wheel   pans (React Flow); over a strip that scrolls, the strip scrolls
//           (a vertical wheel over a sideways strip scrolls it sideways)
// A press on a control keeps its own meaning: typing, a slider, the scrubber,
// a video, and the card's grip bar (React Flow moves the card from there).
// A click that was not a drag is still a click (a scene picture selects it).
//
// The board takes every touch in hand mode (touch-action: none), so what the
// browser did for a finger it does here (review, 2026-10-09, on a touch tablet
// and a phone):
//   - a strip of pictures (data-board-strip: the review card's clips, a past
//     run's pictures) scrolls under a sideways swipe while it can still go that
//     way, and glides on when let go; any other swipe, and one at its end,
//     moves the board. Past the 9th clip the review could not be reached at all.
//   - a slider's thumb slides under a sideways swipe; a tap on its bar puts
//     it there, as it did; any other swipe from it, and a swipe from its bar,
//     moves the board. It filled a band of the phone's board where a finger
//     did nothing else, the pinch included.
//   - a control with no drag of its own is a tap or a drag: the tap is the
//     control's (a button presses, a text box takes the typing, a box ticks),
//     a drag past TOUCH_SLOP moves the board. A text box keeps the finger only
//     while it is being typed in. The browser hands a press beside one, on the
//     card, to it (touch adjustment): 10% of the phone's board did not move.
//   - a button held down (data-board-hold: the strip's conveyor) runs while
//     held; dragged past HOLD_SLOP it lets go and the board moves.
//   - a second finger makes a pinch wherever the two land: with a first one
//     dragging or resting on a control (a video, a text box being typed in, a
//     held button, a card's grip: React Flow drops a card's drag as soon as a
//     second finger touches, which left both fingers doing nothing). Only a
//     scene's reorder or trim handle, whose drags are their own, keeps both.

/** Presses here never move the board: they type, slide, scrub, play, or move a card. */
export const BOARD_KEEP = [
  "input", "textarea", "select", "[contenteditable]:not([contenteditable='false'])",
  "[role='slider']", "[role='scrollbar']", "video", "audio", "iframe",
  ".studio-node-grip", "[data-board-keep]",
].join(", ");
/** Click controls: a mouse press clicks them and the board stays. A finger can
 *  still drag the board from one (a phone scrolls from a button too); the tap
 *  is then dropped. A thumbnail opts out with data-board-grab: it is a picture
 *  you hold to move the board, and a click on it still selects it. */
export const BOARD_CLICKABLE = "button:not([data-board-grab]), [role='button']:not([data-board-grab]), label, summary";
const EDITABLE = "input, textarea, select, [contenteditable]:not([contenteditable='false'])";

/** A strip of pictures a finger scrolls sideways (touchStrip). */
export const BOARD_STRIP = "[data-board-strip]";
/** A button that runs while held (the strip's conveyor): a finger dragging off it moves the board. */
export const BOARD_HOLD = "[data-board-hold]";
/** A text box: on touch, only the one being typed in keeps the finger. */
const TEXT_FIELD = "textarea, [contenteditable]:not([contenteditable='false']), input:not([type]), "
  + "input[type='text'], input[type='search'], input[type='email'], input[type='url'], input[type='tel'], input[type='number'], input[type='password']";
const RANGE = "input[type='range']";
/** Controls whose own drag follows the finger to the end: a second finger never joins one to pinch. */
const OWN_DRAG = "[data-board-keep], [role='slider']";
/** The board's slider thumb, px across (index.css .studio-range), and how far
 *  from its centre a fingertip still holds it. */
const THUMB_PX = 28;
const THUMB_REACH = 24;

/** px a press must travel before it is a drag rather than a click. */
export const MOUSE_SLOP = 3;
export const TOUCH_SLOP = 6;
/** ...and a finger holding a button down (a held finger drifts). */
export const HOLD_SLOP = 12;
/** How long after a drag its click is dropped (a tap's click can trail the finger). */
const CLICK_AFTER_DRAG_MS = 600;
/** A strip let go while moving glides on, slowing by this much every 16 ms. */
const GLIDE_FRICTION = 0.95;

/** keep: the control's own; click: a mouse clicks it (a finger taps it, or
 *  drags the board from it); surface: holds the board; slide: a finger on a
 *  slider (its thumb slides under a sideways swipe: onRangeThumb). */
export type PressKind = "keep" | "click" | "surface" | "slide";

/** What a press on `target` is, looking no further up than `boundary` (the board).
 *  `pointer` is the PointerEvent's pointerType: a finger reads a slider, a
 *  held button and a control with no drag of its own differently (see the top
 *  of this file). */
export function pressKind(target: Element | null, boundary: Element, pointer = "mouse"): PressKind {
  if (!target || !boundary.contains(target)) return "keep";
  const within = (sel: string) => {
    const hit = target.closest(sel);
    return hit && boundary.contains(hit) ? hit : null;
  };
  if (pointer === "touch") {
    if (within(RANGE)) return "slide";
    if (within(BOARD_HOLD)) return "click";
    // A box, a tick box, a list, a text box nobody is typing in: no drag of their own.
    const field = within(EDITABLE);
    if (field && !(field.matches(TEXT_FIELD) && field.contains(document.activeElement))) return "click";
  }
  if (within(BOARD_KEEP)) return "keep";
  if (within(BOARD_CLICKABLE)) return "click";
  return "surface";
}

/** Whether a finger at `clientX` is on the slider's thumb (else on its bar). */
export function onRangeThumb(el: HTMLInputElement, clientX: number): boolean {
  const r = el.getBoundingClientRect();
  if (!(r.width > THUMB_PX)) return true;
  const min = Number(el.min || 0);
  const max = Number(el.max || 100);
  const k = max > min ? Math.min(1, Math.max(0, (Number(el.value) - min) / (max - min))) : 0;
  return Math.abs(clientX - (r.left + THUMB_PX / 2 + k * (r.width - THUMB_PX))) <= THUMB_REACH;
}

/** The strip (BOARD_STRIP) under a finger that swipes `dx` sideways, when it
 *  can still scroll that way (a finger moving left scrolls it on), else null:
 *  the board moves instead. */
export function touchStrip(target: Element | null, boundary: Element, dx: number): HTMLElement | null {
  const strip = target?.closest<HTMLElement>(BOARD_STRIP);
  if (!strip || !boundary.contains(strip) || !dx) return null;
  const max = strip.scrollWidth - strip.clientWidth;
  if (max <= 1) return null;
  return (dx < 0 ? strip.scrollLeft < max - 1 : strip.scrollLeft > 0) ? strip : null;
}

/** The value a slider takes under a finger at `clientX`, on its step. */
export function rangeValueAt(el: HTMLInputElement, clientX: number): string {
  const r = el.getBoundingClientRect();
  const min = Number(el.min || 0);
  const max = Number(el.max || 100);
  const step = Number(el.step);
  const k = r.width > 0 ? Math.min(1, Math.max(0, (clientX - r.left) / r.width)) : 0;
  const raw = min + k * (max - min);
  const v = step > 0 ? Math.min(max, min + Math.round((raw - min) / step) * step) : raw;
  return String(Number(v.toFixed(6)));
}

/** Set a slider the way a finger would: React's onChange hears it. */
function slideTo(el: HTMLInputElement, clientX: number) {
  const value = rangeValueAt(el, clientX);
  if (value === el.value) return;
  Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set?.call(el, value);
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

/**
 * The element under the wheel that takes it itself, or null when the board
 * should pan. Looks from `target` up to its card: a box that can still scroll
 * the wheel's way scrolls natively; a sideways strip (it scrolls only across)
 * takes a vertical wheel as a sideways one. At the end of its travel a box
 * hands the wheel on, as a page does.
 */
export function wheelScroller(target: Element | null, boundary: Element, dx: number, dy: number): { el: HTMLElement; sideways: boolean } | null {
  const vertical = Math.abs(dy) >= Math.abs(dx);
  for (let el = target as HTMLElement | null; el && el !== boundary; el = el.parentElement) {
    if (el.classList?.contains("react-flow__node")) break;
    const cs = getComputedStyle(el);
    const maxX = el.scrollWidth - el.clientWidth;
    const maxY = el.scrollHeight - el.clientHeight;
    const canX = /(auto|scroll)/.test(cs.overflowX) && maxX > 1;
    const canY = /(auto|scroll)/.test(cs.overflowY) && maxY > 1;
    if (!canX && !canY) continue;
    const roomX = (d: number) => (d > 0 ? el.scrollLeft < maxX - 1 : d < 0 ? el.scrollLeft > 0 : false);
    const roomY = (d: number) => (d > 0 ? el.scrollTop < maxY - 1 : d < 0 ? el.scrollTop > 0 : false);
    if (vertical) {
      if (canY && roomY(dy)) return { el, sideways: false };
      if (canX && !canY && roomX(dy)) return { el, sideways: true };
    } else if (canX && roomX(dx)) {
      return { el, sideways: false };
    }
  }
  return null;
}

type Pt = { x: number; y: number };
/** Where the board was, and where the fingers were, when this set of fingers began. */
export type GestureBase = Viewport & { cx: number; cy: number; dist: number };

/**
 * The viewport for a drag (one pointer) or a pinch (two): the board point that
 * was under the fingers' centre stays under it, and the zoom follows the
 * fingers' spread. `origin` is the board's top-left on screen.
 */
export function gestureViewport(base: GestureBase, centre: Pt, dist: number, origin: Pt, minZoom: number, maxZoom: number): Viewport {
  const zoom = dist > 0 && base.dist > 0 ? Math.min(maxZoom, Math.max(minZoom, (base.zoom * dist) / base.dist)) : base.zoom;
  const wx = (base.cx - origin.x - base.x) / base.zoom;
  const wy = (base.cy - origin.y - base.y) / base.zoom;
  return { x: centre.x - origin.x - wx * zoom, y: centre.y - origin.y - wy * zoom, zoom };
}

const centreOf = (pts: Map<number, Pt>): Pt => {
  let x = 0, y = 0;
  pts.forEach((p) => { x += p.x; y += p.y; });
  return { x: x / pts.size, y: y / pts.size };
};
const spreadOf = (pts: Map<number, Pt>): number => {
  if (pts.size < 2) return 0;
  const [a, b] = [...pts.values()];
  return Math.hypot(a.x - b.x, a.y - b.y);
};

/**
 * Wires the hand onto `rootRef` (the element holding the ReactFlow). The root
 * gets `data-board-panning` while the board moves and `data-board-space` while
 * Space is held over it, for the cursor. In "select" mode only the middle
 * button and Space+drag pan here; React Flow keeps its rubber band.
 */
export function useBoardHand(rootRef: RefObject<HTMLElement | null>, opts: { tool: "hand" | "select"; minZoom: number; maxZoom: number }) {
  const { getViewport, setViewport } = useReactFlow();
  const live = useRef({ ...opts, getViewport, setViewport });
  live.current = { ...opts, getViewport, setViewport };

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const flowEl = () => root.querySelector<HTMLElement>(".react-flow");
    const inFlow = (t: EventTarget | null): t is Element => {
      const f = flowEl();
      return !!f && t instanceof Element && f.contains(t);
    };
    const flag = (name: string, on: boolean) => { if (on) root.setAttribute(name, ""); else root.removeAttribute(name); };

    /** board: the board moves; strip: a strip of pictures scrolls (touchStrip);
     *  slide: a slider slides (a finger on its thumb, swiping sideways). */
    type Mode = "board" | "strip" | "slide";
    type Gesture = {
      kind: "mouse" | "touch"; pts: Map<number, Pt>; down: Map<number, Pt>; active: boolean; bySpace: boolean; base: GestureBase;
      mode: Mode; target: Element | null; slider: HTMLInputElement | null; thumb: boolean; hold: HTMLElement | null;
      strip: { el: HTMLElement; from: number; trail: { x: number; t: number }[] } | null;
    };
    type Press = { slider?: HTMLInputElement | null; thumb?: boolean; hold?: HTMLElement | null };
    let g: Gesture | null = null;
    let space = false;
    let dropClickUntil = 0;
    /** Fingers resting on a control: where they are, and whether a second finger
     *  may join them to pinch (not on a control whose own drag follows the finger). */
    const idle = new Map<number, { at: Pt; joins: boolean }>();
    let glide: number | null = null;
    const stopGlide = () => { if (glide !== null) cancelAnimationFrame(glide); glide = null; };

    const rebase = (gest: Gesture) => {
      const c = centreOf(gest.pts);
      gest.base = { ...live.current.getViewport(), cx: c.x, cy: c.y, dist: spreadOf(gest.pts) };
    };
    const begin = (e: PointerEvent, kind: Gesture["kind"], bySpace: boolean, { slider = null, thumb = false, hold = null }: Press = {}): Gesture => {
      const at = { x: e.clientX, y: e.clientY };
      const gest: Gesture = {
        kind, pts: new Map([[e.pointerId, at]]), down: new Map([[e.pointerId, at]]), active: false, bySpace, base: null as unknown as GestureBase,
        mode: "board", target: e.target instanceof Element ? e.target : null, slider, thumb, hold, strip: null,
      };
      rebase(gest);
      g = gest;
      return gest;
    };
    // A held button (the conveyor) whose finger moves the board, or is joined
    // by a second one, lets go: it stops when it loses the finger's capture.
    const letGo = (gest: Gesture) => {
      const el = gest.hold;
      gest.hold = null;
      if (!el) return;
      gest.pts.forEach((_, id) => {
        try {
          if (!el.hasPointerCapture(id)) return;
          el.releasePointerCapture(id);
          // The browser says so only when that finger next moves or lifts: one
          // resting on the button while a second pinches would run it on.
          el.dispatchEvent(new PointerEvent("lostpointercapture", { bubbles: true, pointerId: id, pointerType: "touch" }));
        } catch { /* the press is gone */ }
      });
    };
    const activate = (gest: Gesture, mode: Mode = "board") => {
      gest.active = true;
      gest.mode = mode;
      if (mode !== "board") return;
      letGo(gest);
      flag("data-board-panning", true);
    };
    /** Two fingers: from here they pinch (zoom) and pan the board together. */
    const pinch = (gest: Gesture) => {
      gest.strip = null;
      if (!(gest.active && gest.mode === "slide")) gest.slider = null;
      activate(gest, "board");
      rebase(gest);
    };
    // A strip let go while moving glides on, as a scrolled list does.
    const glideOn = (s: NonNullable<Gesture["strip"]>) => {
      const recent = s.trail.filter((p) => s.trail[s.trail.length - 1].t - p.t <= 100);
      if (recent.length < 2 || typeof requestAnimationFrame !== "function") return;
      const a = recent[0], b = recent[recent.length - 1];
      let v = b.t > a.t ? (a.x - b.x) / (b.t - a.t) : 0; // px of scroll per ms
      let last = performance.now();
      const step = (now: number) => {
        const dt = Math.min(64, now - last);
        last = now;
        const before = s.el.scrollLeft;
        s.el.scrollLeft = before + v * dt;
        v *= GLIDE_FRICTION ** (dt / 16);
        glide = Math.abs(v) > 0.02 && s.el.scrollLeft !== before ? requestAnimationFrame(step) : null;
      };
      glide = requestAnimationFrame(step);
    };
    const finish = () => {
      if (!g) return;
      if (g.active || g.bySpace) dropClickUntil = performance.now() + CLICK_AFTER_DRAG_MS;
      if (g.mode === "strip" && g.strip) glideOn(g.strip);
      if (g.slider) g.slider.dispatchEvent(new Event("change", { bubbles: true }));
      g = null;
      flag("data-board-panning", false);
    };

    // Any new press anywhere: the click of an earlier drag is not coming any more,
    // and a gliding strip stops under the finger.
    const onAnyPointerDown = () => { dropClickUntil = 0; stopGlide(); };

    const onPointerDown = (e: PointerEvent) => {
      // A press while a drag of the same kind is still open means its release
      // was lost (the window lost focus mid-drag): that drag is over. The mouse
      // presses again only after letting go (a second button is a move), and a
      // first finger (isPrimary) means no other finger is down.
      if (g && (e.pointerType === "touch" ? g.kind === "touch" && e.isPrimary : g.kind === "mouse")) finish();
      if (e.pointerType === "touch" && e.isPrimary) idle.clear();
      if (!inFlow(e.target)) return;
      const kind = pressKind(e.target, flowEl()!, e.pointerType);
      if (e.pointerType === "touch") {
        const at = { x: e.clientX, y: e.clientY };
        if (g?.kind === "touch") {
          // A second finger: from here the fingers pinch (zoom) and pan together.
          // It is the pinch's: what it landed on never hears it (a held button
          // would run under it).
          e.stopPropagation();
          g.pts.set(e.pointerId, at);
          g.down.set(e.pointerId, at);
          pinch(g);
          return;
        }
        const hand = live.current.tool === "hand";
        // A second finger beside one resting on a control (a video, a text box
        // being typed in, a card's grip): the two pinch the board, wherever
        // they landed.
        const first = idle.size === 1 ? [...idle.entries()][0] : null;
        if (!g && hand && first && first[1].joins) {
          e.stopPropagation();
          idle.clear();
          const two = begin(e, "touch", false);
          two.pts.set(first[0], first[1].at);
          two.down.set(first[0], first[1].at);
          pinch(two);
          return;
        }
        if (g || idle.size || !hand || kind === "keep") {
          const own = e.target instanceof Element && !!e.target.closest(OWN_DRAG);
          idle.set(e.pointerId, { at, joins: hand && !own });
          return;
        }
        const target = e.target as Element;
        const slider = kind === "slide" ? target.closest<HTMLInputElement>(RANGE) : null;
        const hold = target.closest<HTMLElement>(BOARD_HOLD);
        begin(e, "touch", false, { slider, thumb: !!slider && onRangeThumb(slider, e.clientX), hold: hold && flowEl()!.contains(hold) ? hold : null });
        return;
      }
      if (g) return;
      const middle = e.button === 1;
      if (!middle && e.button !== 0) return;
      const bySpace = !middle && space;
      if (!middle && !bySpace && (live.current.tool !== "hand" || kind !== "surface")) return;
      // No text selection, no picture or link dragged out, no middle-click
      // autoscroll, and React Flow's own pan never starts on top of this one.
      e.preventDefault();
      // ...which also keeps the focus where it was: a text box loses it, as on
      // any press outside it, so Space then pans instead of typing.
      const focused = document.activeElement;
      if (focused instanceof HTMLElement && focused.matches(EDITABLE) && !focused.contains(e.target)) focused.blur();
      begin(e, "mouse", bySpace);
    };
    const onPointerMove = (e: PointerEvent) => {
      const resting = idle.get(e.pointerId);
      if (resting) resting.at = { x: e.clientX, y: e.clientY };
      if (!g || !g.pts.has(e.pointerId)) return;
      // The mouse was let go where no release reached the page.
      if (g.kind === "mouse" && e.pointerType === "mouse" && e.buttons === 0) { finish(); return; }
      g.pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const d0 = g.down.get(e.pointerId)!;
      if (!g.active) {
        const dx = e.clientX - d0.x, dy = e.clientY - d0.y;
        if (Math.hypot(dx, dy) < (g.kind === "mouse" ? MOUSE_SLOP : g.hold ? HOLD_SLOP : TOUCH_SLOP)) return;
        // One finger swiping sideways: a slider's thumb slides, a strip that
        // can still go that way scrolls; anything else moves the board.
        const sideways = g.kind === "touch" && g.pts.size === 1 && Math.abs(dx) > Math.abs(dy);
        const strip = sideways ? touchStrip(g.target, flowEl()!, dx) : null;
        if (sideways && g.slider && g.thumb) activate(g, "slide");
        else if (strip) {
          g.strip = { el: strip, from: strip.scrollLeft, trail: [] };
          activate(g, "strip");
        } else {
          g.slider = null;
          activate(g, "board");
        }
      }
      if (g.mode === "slide" && g.slider) { slideTo(g.slider, e.clientX); return; }
      if (g.mode === "strip" && g.strip) {
        g.strip.el.scrollLeft = g.strip.from - (e.clientX - d0.x);
        g.strip.trail.push({ x: e.clientX, t: e.timeStamp || performance.now() });
        if (g.strip.trail.length > 8) g.strip.trail.shift();
        return;
      }
      const r = flowEl()?.getBoundingClientRect();
      const { minZoom, maxZoom } = live.current;
      void live.current.setViewport(gestureViewport(g.base, centreOf(g.pts), spreadOf(g.pts), { x: r?.left ?? 0, y: r?.top ?? 0 }, minZoom, maxZoom));
    };
    const onPointerEnd = (e: PointerEvent) => {
      idle.delete(e.pointerId);
      if (!g || !g.pts.has(e.pointerId)) return;
      // A tap on a slider puts it where the finger was, as the browser did.
      if (!g.active && g.slider && e.type === "pointerup") slideTo(g.slider, g.down.get(e.pointerId)!.x);
      g.pts.delete(e.pointerId);
      g.down.delete(e.pointerId);
      if (g.pts.size) { rebase(g); return; }
      finish();
    };
    // The mouse events of a press the hand took never reach the cards (nor
    // React Flow's middle-button pan on a card).
    const onMouseDown = (e: MouseEvent) => { if (g?.kind === "mouse") { e.preventDefault(); e.stopPropagation(); } };
    const onDragStart = (e: DragEvent) => { if (g) e.preventDefault(); };
    // A finger on a grip bar: the browser holds back a touch's first moves (its
    // tap slop) unless the touch start is taken, and the card then trails the
    // finger by them. The grip has no tap of its own to lose. On a slider, the
    // browser would jump it to the finger at once, before the swipe shows its
    // way: the hand slides it instead.
    const onTouchStart = (e: TouchEvent) => {
      if (!e.cancelable || !(e.target instanceof Element)) return;
      if (e.target.closest(".studio-node-grip")) e.preventDefault();
      else if (live.current.tool === "hand" && inFlow(e.target) && e.target.closest(RANGE)) e.preventDefault();
    };
    const onSelectStart = (e: Event) => { if (g) e.preventDefault(); };
    const onClick = (e: MouseEvent) => {
      if (!dropClickUntil) return;
      const due = performance.now() < dropClickUntil;
      dropClickUntil = 0;
      // A keyboard click (detail 0) is never the end of a drag.
      if (!due || e.detail === 0) return;
      e.preventDefault();
      e.stopImmediatePropagation();
    };

    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || !inFlow(e.target)) return; // Ctrl+wheel / a trackpad pinch: React Flow zooms
      const k = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 400 : 1;
      const hit = wheelScroller(e.target, flowEl()!, e.deltaX * k, e.deltaY * k);
      if (!hit) return; // React Flow pans the board
      e.stopPropagation(); // ...unless a strip under the wheel takes it
      if (hit.sideways) {
        e.preventDefault();
        hit.el.scrollLeft += e.deltaY * k;
      }
    };

    const editable = (t: EventTarget | null) => t instanceof Element && !!t.closest(EDITABLE);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code !== "Space" || e.ctrlKey || e.metaKey || e.altKey || editable(e.target)) return;
      if (!space && !root.matches(":hover")) return;
      e.preventDefault(); // no page scroll, and no focused button pressed
      if (!space) { space = true; flag("data-board-space", true); }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code !== "Space" || !space) return;
      e.preventDefault();
      space = false;
      flag("data-board-space", false);
    };
    const onBlur = () => {
      space = false;
      flag("data-board-space", false);
      idle.clear();
      stopGlide();
      finish();
    };

    root.addEventListener("pointerdown", onPointerDown, true);
    root.addEventListener("mousedown", onMouseDown, true);
    root.addEventListener("dragstart", onDragStart, true);
    root.addEventListener("selectstart", onSelectStart, true);
    root.addEventListener("touchstart", onTouchStart, { capture: true, passive: false });
    root.addEventListener("wheel", onWheel, { capture: true, passive: false });
    window.addEventListener("pointerdown", onAnyPointerDown, true);
    window.addEventListener("pointermove", onPointerMove, true);
    window.addEventListener("pointerup", onPointerEnd, true);
    window.addEventListener("pointercancel", onPointerEnd, true);
    window.addEventListener("click", onClick, true);
    window.addEventListener("auxclick", onClick, true);
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("keyup", onKeyUp, true);
    window.addEventListener("blur", onBlur);
    return () => {
      root.removeEventListener("pointerdown", onPointerDown, true);
      root.removeEventListener("mousedown", onMouseDown, true);
      root.removeEventListener("dragstart", onDragStart, true);
      root.removeEventListener("selectstart", onSelectStart, true);
      root.removeEventListener("touchstart", onTouchStart, true);
      root.removeEventListener("wheel", onWheel, true);
      window.removeEventListener("pointerdown", onAnyPointerDown, true);
      window.removeEventListener("pointermove", onPointerMove, true);
      window.removeEventListener("pointerup", onPointerEnd, true);
      window.removeEventListener("pointercancel", onPointerEnd, true);
      window.removeEventListener("click", onClick, true);
      window.removeEventListener("auxclick", onClick, true);
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("keyup", onKeyUp, true);
      window.removeEventListener("blur", onBlur);
      stopGlide();
      flag("data-board-panning", false);
      flag("data-board-space", false);
    };
  }, [rootRef]);
}
